import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import {
  ChevronDown, ChevronRight,
  LayoutDashboard, TrendingUp, Users, Boxes, MapPin,
  Zap, Clock, Grid3x3, Award, PieChart, Activity,
  Building2, Package, Radar, GitBranch, Stethoscope,
  MessageCircle, Trophy,
} from 'lucide-react';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';

/**
 * Reports left-rail configuration. Each section groups related reports.
 * A `key` maps to the internal Tab value in ReportsPage.
 */
export const REPORT_SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    color: '#2D3982',
    reports: [
      { key: 'executive',   label: 'Executive Summary', icon: Award,     desc: 'Top KPIs & headline charts' },
      { key: 'funnel',      label: 'Sales Funnel',      icon: PieChart,  desc: 'Drop-off between stages' },
      { key: 'yoy',         label: 'Year-over-Year',    icon: TrendingUp,desc: 'Monthly current vs prior' },
      { key: 'trends',      label: 'Volume Trends',     icon: Activity,  desc: 'Weekly / monthly volume' },
    ],
  },
  {
    id: 'patients',
    label: 'Patients',
    icon: Users,
    color: '#059669',
    reports: [
      { key: 'lapsed',      label: 'Lapsed / Re-engage', icon: Zap,     desc: '30-180+ day inactive leads' },
      { key: 'customers',   label: 'Customer Cohorts',   icon: Grid3x3, desc: 'Repeat rate & frequency' },
      { key: 'cohort',      label: 'Retention Cohorts',  icon: Radar,   desc: 'Monthly acquisition x sold' },
      { key: 'aging',       label: 'Aging Pipeline',     icon: Clock,   desc: 'Days-open by disposition' },
    ],
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: TrendingUp,
    color: '#B45309',
    reports: [
      { key: 'audiologists', label: 'Audiologists',     icon: Stethoscope, desc: 'Sales leaderboard' },
      { key: 'consultants',  label: 'Consultants',      icon: GitBranch,   desc: 'Consultant leaderboard' },
      { key: 'clinics',      label: 'Clinics',          icon: Building2,   desc: 'Clinic-level P&L view' },
      { key: 'clusters',     label: 'Clusters',         icon: Grid3x3,     desc: 'Regional rollups' },
    ],
  },
  {
    id: 'product',
    label: 'Product & Sales',
    icon: Boxes,
    color: '#7C3AED',
    reports: [
      { key: 'items',        label: 'Items / Devices',  icon: Package, desc: 'Best & worst SKUs' },
      { key: 'sources',      label: 'Source Mix',       icon: PieChart,desc: 'Channel volume + conv' },
      { key: 'source-effectiveness', label: 'Source Effectiveness', icon: Zap, desc: 'Sub-source deep-dive' },
    ],
  },
  {
    id: 'geographic',
    label: 'Geographic',
    icon: MapPin,
    color: '#DC2626',
    reports: [
      { key: 'geo',          label: 'Clinic Heatmap',   icon: MapPin,  desc: 'Volume + revenue by clinic' },
    ],
  },
];

export function ReportsRail({ active, onSelect, counts = {}, collapsedSections, onToggleSection, kpi }) {
  const [leaders, setLeaders] = useState([]);
  const [waTarget, setWaTarget] = useState(null); // {phone, name}

  useEffect(() => {
    let cancelled = false;
    api.get('/reports/by-audiologist', { params: { limit: 3 } })
      .then(async (r) => {
        if (cancelled) return;
        const top = r.data.data || [];
        // Try to enrich with mobile from fundle_users (via /users if available)
        try {
          const u = await api.get('/users');
          const byEmail = Object.fromEntries((u.data || []).map(x => [(x.email || '').toLowerCase(), x]));
          top.forEach((l) => {
            const local = byEmail[(l.audiologist || '').toLowerCase()];
            if (local?.phone) l.phone = local.phone;
          });
        } catch { /* users API is manager-only; ignore */ }
        setLeaders(top);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <aside
      className="w-[240px] shrink-0 border-r border-stone-200 bg-white overflow-y-auto hc-scrollbar"
      data-testid="reports-rail"
    >
      {/* Live KPI header at top */}
      <div className="px-3 py-3 border-b border-stone-200" style={{ background: 'linear-gradient(180deg, #F1F3FA 0%, #FFFFFF 100%)' }}>
        <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#2D3982' }}>Live snapshot</div>
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          <MiniKpi label="Total" value={kpi?.total_tickets} color="#2D3982" tid="rail-kpi-total" />
          <MiniKpi label="Sold" value={kpi?.sold} color="#059669" tid="rail-kpi-sold" />
          <MiniKpi label="Conv" value={kpi ? `${kpi.conversion_rate}%` : null} color="#B45309" tid="rail-kpi-conv" />
          <MiniKpi label="Lost" value={kpi?.lost} color="#DC2626" tid="rail-kpi-lost" />
        </div>
      </div>

      {/* Sales Leaderboard — Live (Top 3 audiologists) */}
      {leaders.length > 0 && (
        <div className="px-3 py-2.5 border-b border-stone-200 bg-white" data-testid="rail-leaderboard">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Trophy className="w-3 h-3 text-amber-600" />
            <span className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">Top audiologists</span>
          </div>
          <div className="space-y-1">
            {leaders.map((l, i) => (
              <div key={l.audiologist || i}
                className="flex items-center gap-1.5 py-1 px-1 rounded hover:bg-stone-50"
                data-testid={`rail-leader-${i}`}>
                <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0"
                  style={{ background: ['#FCD34D','#D1D5DB','#F5CBA7'][i] || '#E5E7EB', color: '#78350F' }}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-stone-800 truncate leading-tight">
                    {l.name || (l.audiologist || '').split('@')[0]}
                  </div>
                  <div className="text-[9px] text-stone-500 tabular-nums leading-tight">
                    {l.sold?.toLocaleString?.() || l.sold} sold · {l.conversion || 0}%
                  </div>
                </div>
                {l.phone && (
                  <button
                    onClick={() => setWaTarget({ phone: l.phone, name: l.name })}
                    title={`WhatsApp ${l.name}`}
                    data-testid={`rail-leader-wa-${i}`}
                    className="w-5 h-5 rounded flex items-center justify-center hover:bg-emerald-50 transition-colors">
                    <MessageCircle className="w-3 h-3 text-emerald-600" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {waTarget && (
        <WhatsAppSendDialog
          open={!!waTarget}
          onOpenChange={(v) => !v && setWaTarget(null)}
          to={waTarget.phone}
          patientName={waTarget.name}
        />
      )}

      {REPORT_SECTIONS.map(sec => {
        const collapsed = collapsedSections?.includes(sec.id);
        const Chev = collapsed ? ChevronRight : ChevronDown;
        return (
          <div key={sec.id} className="border-b border-stone-100 last:border-b-0">
            <button
              onClick={() => onToggleSection?.(sec.id)}
              data-testid={`reports-rail-section-${sec.id}`}
              className="w-full flex items-center gap-1.5 px-3 py-2 text-left hover:bg-stone-50 transition-colors"
            >
              <Chev className="w-3.5 h-3.5 text-stone-500" />
              <sec.icon className="w-3.5 h-3.5" style={{ color: sec.color }} />
              <span className="flex-1 text-xs font-bold uppercase tracking-wider text-stone-800">{sec.label}</span>
              <span className="text-[10px] text-stone-400 font-mono">{sec.reports.length}</span>
            </button>
            {!collapsed && (
              <ul className="pb-1">
                {sec.reports.map(r => {
                  const isActive = active === r.key;
                  const count = counts[r.key];
                  return (
                    <li key={r.key}>
                      <button
                        onClick={() => onSelect?.(r.key)}
                        data-testid={`reports-rail-item-${r.key}`}
                        className={cn(
                          "w-full flex items-start gap-2 pl-8 pr-2 py-1.5 text-left hc-rail-item text-xs",
                          isActive && "active"
                        )}
                      >
                        <r.icon className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: sec.color }} />
                        <div className="flex-1 min-w-0">
                          <div className={cn("text-xs leading-tight", isActive ? "font-semibold text-[color:var(--hc-primary-dark)]" : "text-stone-800")}>
                            {r.label}
                          </div>
                          {r.desc && (
                            <div className="text-[10px] text-stone-500 truncate">{r.desc}</div>
                          )}
                        </div>
                        {count != null && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded tabular-nums" style={{
                            background: isActive ? '#2D3982' : '#E7E5E4',
                            color: isActive ? '#fff' : '#57534E',
                          }}>{typeof count === 'number' ? count.toLocaleString() : count}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </aside>
  );
}

function MiniKpi({ label, value, color, tid }) {
  return (
    <div className="rounded p-1.5 bg-white border border-stone-200" data-testid={tid}>
      <div className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
      <div className="text-sm font-bold tabular-nums" style={{ color }}>
        {value != null ? (typeof value === 'number' ? value.toLocaleString() : value) : '—'}
      </div>
    </div>
  );
}
